import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useToasts } from '@/composables/useToasts'
import { useLibraryStore } from '@/pages/infoLibrary/stores/LibraryStore'

import {
  getCard,
  viewCard as viewCardRepository,
  getTab,
  getAllCards
} from '@/pages/infoLibrary/services/library.repository'

import type {
  Card,
  GetAllCardsRequest
} from '@/pages/infoLibrary/config/library.types'
import { convertReadonlyCardToRaw } from '../config/library.utils'
import { DateTime } from 'luxon'
import { useCommonStore } from '@/stores/CommonStore'

type HydrateSelectedCardOptions = { forceRefresh?: boolean }

/* =========================================================
   Composable
========================================================= */

export function useLibraryEntry() {
  const { t } = useI18n()

  const { fetchBadgeCounts } = useCommonStore()

  const store = useLibraryStore()
  const {
    selectedCard,
    selectedCardId,
    cardForEdit,
    tabForEdit,
    selectedTabId,
    searchTerm,
    filterCardsBy,
    sortCardsBy,
    cards
  } = storeToRefs(store)
  const {
    setSelectedCard,
    setIsFetchingDetails,
    setCardForEdit,
    setTabForEdit,
    replaceCard,
    setCards
  } = store
  const { showErrorToast } = useToasts()

  const isLoadingEntry = ref(false)
  const isStale = ref(false)

  const card = computed(() => selectedCard.value)

  function buildGetAllCardsRequest(): GetAllCardsRequest {
    return {
      tabId: selectedTabId.value,
      ...(searchTerm.value && { searchTerm: searchTerm.value }),
      filterBy: filterCardsBy.value,
      sortBy: sortCardsBy.value
    }
  }

  function findCard(cardId: number): Card | null {
    const card = cards.value.find((card) => card.itemId === cardId) ?? null

    return card ? convertReadonlyCardToRaw(card) : null
  }

  function showLoadErrorToast(message: string): void {
    showErrorToast(message)
  }

  function getLoadErrorMessage(params: {
    error: unknown
    offlineKey: string
    genericKey: string
  }): string {
    const { error, offlineKey, genericKey } = params

    return error instanceof Error &&
      error.message === 'library_offline_no_cache'
      ? t(offlineKey)
      : t(genericKey)
  }

  /* =========================================================
     LOAD CARD (fetch for edit form)
  ========================================================= */
  async function loadCard(cardId: number): Promise<void> {
    if (!cardId || cardId <= 0) {
      console.warn('[useLibraryEntry] Invalid card ID:', cardId)
      return
    }

    isLoadingEntry.value = true

    try {
      const result = await getCard({ cardId })
      setCardForEdit(result.data)
      isStale.value = result.isStale
    } catch (error: unknown) {
      const shouldShowError = cardForEdit.value.itemId !== cardId

      if (shouldShowError) {
        showLoadErrorToast(
          getLoadErrorMessage({
            error,
            offlineKey: 'infoLibrary.errors.loadCardOffline',
            genericKey: 'infoLibrary.errors.loadCard'
          })
        )
      }
    } finally {
      isLoadingEntry.value = false
    }
  }

  async function hydrateSelectedCard(
    cardId = selectedCardId.value,
    options?: HydrateSelectedCardOptions
  ): Promise<Card | null> {
    if (cardId == null) return null

    setIsFetchingDetails(true)

    try {
      const result = await getCard({ cardId }, options)
      replaceCard(result.data)

      if (selectedCardId.value === cardId) {
        setSelectedCard(result.data)
      }

      isStale.value = result.isStale

      return result.data
    } catch (error: unknown) {
      if (selectedCard.value?.itemId !== cardId) {
        showLoadErrorToast(
          getLoadErrorMessage({
            error,
            offlineKey: 'infoLibrary.errors.loadCardOffline',
            genericKey: 'infoLibrary.errors.loadCard'
          })
        )
      }

      return null
    } finally {
      setIsFetchingDetails(false)
    }
  }

  async function selectCard(
    card: Card,
    options: { markViewed?: boolean } = {}
  ): Promise<void> {
    const previouslySelectedCardId = selectedCardId.value

    setSelectedCard(card)

    if (options.markViewed) {
      await viewCard()
      return
    }

    if (card.itemId === previouslySelectedCardId) return

    await hydrateSelectedCard(card.itemId)
  }

  /* =========================================================
     VIEW CARD (mark as viewed + update in-place)
  ========================================================= */
  async function viewCard(): Promise<void> {
    const cardId = selectedCardId.value

    if (cardId == null) return

    const targetCard =
      selectedCard.value?.itemId === cardId
        ? convertReadonlyCardToRaw(selectedCard.value)
        : findCard(cardId)

    try {
      setIsFetchingDetails(true)

      const response = await viewCardRepository({ cardId })
      const baseCard = targetCard ?? response
      const newCard: Card = {
        ...baseCard,
        ...response,
        itemId: cardId,
        viewedWhen:
          response.updatedWhen?.plus({ milliseconds: 1 }) ??
          DateTime.now().plus({ milliseconds: 1 })
      }

      replaceCard(newCard)

      if (selectedCardId.value === cardId) {
        setSelectedCard(newCard)
      }

      await fetchBadgeCounts()
    } catch {
      try {
        const cardsResult = await getAllCards(buildGetAllCardsRequest(), {
          forceRefresh: true
        })
        setCards(cardsResult.data)

        if (selectedCardId.value !== cardId) {
          return
        }

        const nextCard = findCard(cardId)

        if (nextCard) {
          setSelectedCard(
            nextCard.viewedWhen != null
              ? nextCard
              : { ...nextCard, viewedWhen: DateTime.now() }
          )
        } else {
          clearEntry()
        }
      } catch {
        // Already logged
      }
    } finally {
      setIsFetchingDetails(false)
    }
  }

  /* =========================================================
     LOAD TAB FOR EDIT
  ========================================================= */
  async function loadTabForEdit(): Promise<void> {
    try {
      const result = await getTab({ tabId: selectedTabId.value })
      setTabForEdit(result?.data ?? null)
    } catch (error: unknown) {
      if (tabForEdit.value.itemId !== selectedTabId.value) {
        showLoadErrorToast(
          getLoadErrorMessage({
            error,
            offlineKey: 'infoLibrary.errors.loadTabOffline',
            genericKey: 'infoLibrary.errors.loadTab'
          })
        )
      }
    }
  }

  /* =========================================================
     CLEAR ENTRY
  ========================================================= */
  function clearEntry(): void {
    setSelectedCard(null)
    isStale.value = false
    isLoadingEntry.value = false
    setIsFetchingDetails(false)
  }

  return {
    card,
    cardForEdit,
    tabForEdit,
    isLoadingEntry,
    isStale,
    loadCard,
    hydrateSelectedCard,
    selectCard,
    viewCard,
    loadTabForEdit,
    clearEntry
  }
}

export type UseLibraryEntryReturn = ReturnType<typeof useLibraryEntry>
